// WEBHOOKS-IN (design 2026-10-27 §2, §7; Andrei 2026-10-27: no automation rules in the path):
// incoming webhooks. A manager makes a hook (a URL and a secret, shown once) aimed at a digital
// employee (optionally "for agent X", an AG reporting to it, and a board). A sender posts to
// `hooks/in/{id}` signed with the hook's secret; the hook logs the delivery, and the delivery
// becomes one task "<hook name> · <received time>" on the DE's seat with the fixed data-not-
// instructions preamble and the payload attached. The DE's skills and role decide what to do.
// Signed modes only (CompanyOS, Stripe, GitHub); the design's unsigned token mode is not built
// (a `token` verification would be a fourth `InboundWebhookVerification` value and a branch in
// the service's `verifyInbound`).
import { z } from "zod";
import {
  InboundWebhookScreenMode,
  InboundWebhookStatus,
  InboundWebhookVerification,
} from "../enums";
import { boundedRecord, FreeText, LIMITS, text, Uuid } from "../primitives";
import { InboundDelivery, InboundWebhook } from "../rows";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** At most this many incoming webhooks per company. */
export const MAX_INBOUND_WEBHOOKS = 100;
/** The per-hook body cap's range (bytes): 1 KiB … 1 MiB, default 256 KiB. */
export const INBOUND_MAX_BYTES = { min: 1024, max: 1_048_576, default: 262_144 } as const;

/** A CIDR (`203.0.113.0/24`, `2001:db8::/32`) or a single address. */
const Cidr = z.string().trim().max(64).refine(isCidr, "an IP address or CIDR");

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

/** An IPv6 address in the URL parser's canonical spelling (no zone, no brackets), or null. */
function ipv6Canonical(s: string): string | null {
  if (!/^[0-9a-fA-F:.]+$/.test(s) || !s.includes(":")) return null;
  try {
    const host = new URL(`http://[${s}]/`).hostname;
    return host.length > 2 ? host.slice(1, -1) : null;
  } catch {
    return null;
  }
}

/**
 * An address or `address/prefix` with the prefix inside the family (≤ 32 / ≤ 128). Shared by the
 * API and the web twin; the API then normalises it (`::ffff:` mapped, duplicates dropped).
 */
export function isCidr(value: string): boolean {
  const parts = value.split("/");
  if (parts.length > 2) return false;
  const [addr = "", bits] = parts;
  const v6 = IPV4.test(addr) ? null : ipv6Canonical(addr);
  const max = IPV4.test(addr) ? 32 : v6 ? 128 : 0;
  if (max === 0) return false;
  // an IPv4-mapped IPv6 address (any spelling) is stored as IPv4: write its range as IPv4
  if (bits !== undefined && v6?.startsWith("::ffff:")) return false;
  return bits === undefined || (/^\d{1,3}$/.test(bits) && Number(bits) <= max);
}

/**
 * A secret the sender gave (Stripe shows its endpoint secret; a CompanyOS or GitHub hook may take
 * one too). Absent: the server makes one and returns it once.
 */
const SenderSecret = z
  .string()
  .trim()
  .min(16)
  .max(256)
  .regex(/^[\x21-\x7e]+$/, "printable ASCII");

const settings = {
  name: text(LIMITS.name),
  description: FreeText.nullable().optional(),
  /** The DE (its membership) whose seat gets each delivery's task. */
  target_membership_id: Uuid,
  /** An AG reporting to that DE the work is for (the task says "for your agent X"). */
  for_agent_id: Uuid.nullable().optional(),
  /** Where the task lands (absent: the company's first team board). */
  board_id: Uuid.nullable().optional(),
  allowed_cidrs: z.array(Cidr).max(20).optional(),
  max_bytes: z.int().min(INBOUND_MAX_BYTES.min).max(INBOUND_MAX_BYTES.max).optional(),
  rate_per_minute: z.int().min(1).max(600).optional(),
  payload_retention_days: z.int().min(1).max(90).optional(),
  screen_mode: InboundWebhookScreenMode.optional(),
};

export const InboundWebhookCreate = z
  .strictObject({
    ...settings,
    verification: InboundWebhookVerification.optional(),
    secret: SenderSecret.optional(),
  })
  .meta({ id: "InboundWebhookCreate" });

/** `status`: pause (503 to senders), disable (410) or resume. The secret changes only by rotate. */
export const InboundWebhookPatch = z
  .strictObject({ ...settings, status: InboundWebhookStatus.optional() })
  .partial()
  .meta({ id: "InboundWebhookPatch" });

export const InboundWebhookRotate = z
  .strictObject({ secret: SenderSecret.optional() })
  .meta({ id: "InboundWebhookRotate" });

/** A hook as its row shows it: the endpoint path, whom it is for, and its last day. */
export const InboundWebhookView = InboundWebhook.extend({
  /** Where senders post (append to the API's public origin). */
  endpoint_path: z.string(),
  /** The target DE's display name. */
  target_name: z.string(),
  /** The AG's name, when `for_agent_id` is set. */
  for_agent_name: z.string().nullable(),
  /** The board's name, when `board_id` is set. */
  board_name: z.string().nullable(),
  received_24h: z.int().min(0),
}).meta({ id: "InboundWebhookView" });
export type InboundWebhookView = z.infer<typeof InboundWebhookView>;

/** The secret is returned once (null when the request gave it). */
export const InboundWebhookCreated = z
  .object({ inbound_webhook: InboundWebhookView, secret: z.string().nullable() })
  .meta({ id: "InboundWebhookCreated" });

/** One delivery with its stored body (null once purged, or never kept). */
export const InboundDeliveryDetail = InboundDelivery.extend({
  body: z.unknown().nullable(),
}).meta({ id: "InboundDeliveryDetail" });

export const InboundSampleRequest = z
  .strictObject({
    /** A JSON object to send (absent: a small example). */
    payload: boundedRecord(z.unknown()).optional(),
  })
  .meta({ id: "InboundSampleRequest" });

/** What the endpoint answers a sender. */
export const InboundReceipt = z
  .object({ receipt_id: Uuid, status: z.enum(["accepted", "duplicate"]) })
  .meta({ id: "InboundReceipt" });

const DeliveryParams = z.object({ co: Uuid, id: Uuid, delivery_id: Uuid });

export const inboundWebhookRoutes = {
  "inboundWebhooks.list": defineRoute({
    ...core,
    id: "inboundWebhooks.list",
    method: "get",
    path: `${base}/inbound-webhooks`,
    tag: "webhooks",
    summary: "List incoming webhooks",
    access: "manager",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(InboundWebhookView) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "inboundWebhooks.create": defineRoute({
    ...core,
    id: "inboundWebhooks.create",
    method: "post",
    path: `${base}/inbound-webhooks`,
    tag: "webhooks",
    summary: "Create an incoming webhook; the secret is returned once",
    access: "manager",
    params: CompanyParams,
    body: InboundWebhookCreate,
    response: { status: 201, schema: InboundWebhookCreated },
    errors: [],
    emits: ["inbound_webhook.created"],
  }),
  "inboundWebhooks.get": defineRoute({
    ...core,
    id: "inboundWebhooks.get",
    method: "get",
    path: `${base}/inbound-webhooks/{id}`,
    tag: "webhooks",
    summary: "Get an incoming webhook (never its secret)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: InboundWebhookView },
    errors: [],
    emits: [],
  }),
  "inboundWebhooks.update": defineRoute({
    ...core,
    id: "inboundWebhooks.update",
    method: "patch",
    path: `${base}/inbound-webhooks/{id}`,
    tag: "webhooks",
    summary: "Change settings or the target; pause, disable or resume",
    access: "manager",
    params: CompanyItemParams,
    body: InboundWebhookPatch,
    response: { status: 200, schema: InboundWebhookView },
    errors: [],
    emits: [
      "inbound_webhook.updated",
      "inbound_webhook.paused",
      "inbound_webhook.resumed",
      "inbound_webhook.disabled",
    ],
  }),
  "inboundWebhooks.delete": defineRoute({
    ...core,
    id: "inboundWebhooks.delete",
    method: "delete",
    path: `${base}/inbound-webhooks/{id}`,
    tag: "webhooks",
    summary: "Remove an incoming webhook and its delivery log (its tasks stay)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["inbound_webhook.deleted"],
  }),
  "inboundWebhooks.rotateSecret": defineRoute({
    ...core,
    id: "inboundWebhooks.rotateSecret",
    method: "post",
    path: `${base}/inbound-webhooks/{id}/rotate-secret`,
    tag: "webhooks",
    summary: "Replace the secret (the old one verifies for 24 h); the new one is returned once",
    access: "manager",
    params: CompanyItemParams,
    body: InboundWebhookRotate,
    response: { status: 200, schema: InboundWebhookCreated },
    errors: [],
    emits: ["inbound_webhook.secret_rotated"],
  }),
  "inboundWebhooks.deliveries": defineRoute({
    ...core,
    id: "inboundWebhooks.deliveries",
    method: "get",
    path: `${base}/inbound-webhooks/{id}/deliveries`,
    tag: "webhooks",
    summary: "Delivery log, newest first (without bodies)",
    access: "manager",
    params: CompanyItemParams,
    query: PageQuery,
    response: { status: 200, schema: page(InboundDelivery) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "inboundWebhooks.getDelivery": defineRoute({
    ...core,
    id: "inboundWebhooks.getDelivery",
    method: "get",
    path: `${base}/inbound-webhooks/{id}/deliveries/{delivery_id}`,
    tag: "webhooks",
    summary: "One delivery with its stored body",
    access: "manager",
    params: DeliveryParams,
    response: { status: 200, schema: InboundDeliveryDetail },
    errors: [],
    emits: [],
  }),
  "inboundWebhooks.replayDelivery": defineRoute({
    ...core,
    id: "inboundWebhooks.replayDelivery",
    method: "post",
    path: `${base}/inbound-webhooks/{id}/deliveries/{delivery_id}/replay`,
    tag: "webhooks",
    summary: "Fire a stored delivery's body again, as a new delivery",
    access: "manager",
    params: DeliveryParams,
    response: { status: 202, schema: InboundDelivery },
    errors: ["conflict"],
    emits: ["inbound_webhook.received"],
  }),
  "inboundWebhooks.sample": defineRoute({
    ...core,
    id: "inboundWebhooks.sample",
    method: "post",
    path: `${base}/inbound-webhooks/{id}/sample`,
    tag: "webhooks",
    summary: "Send a sample delivery (it becomes a task for the target DE, like a real one)",
    access: "manager",
    params: CompanyItemParams,
    body: InboundSampleRequest,
    response: { status: 202, schema: InboundDelivery },
    errors: ["conflict"],
    emits: ["inbound_webhook.received"],
  }),
  "inboundWebhooks.receive": defineRoute({
    ...core,
    id: "inboundWebhooks.receive",
    method: "post",
    path: `${base}/hooks/in/{id}`,
    tag: "webhooks",
    summary: "A sender's delivery to an incoming webhook (signed; JSON)",
    description:
      "Called by the outside system, never by a person. The hook's verification mode decides the " +
      "header: `X-CompanyOS-Signature: t=<unix>,v1=<hex>` (HMAC-SHA256 of `<t>.<raw body>`, 300 s " +
      "window), Stripe's `Stripe-Signature` (the same scheme) or GitHub's `X-Hub-Signature-256: " +
      "sha256=<hex>` (HMAC-SHA256 of the raw body). Until the signature verifies every refusal " +
      "is 401 alike: an unknown hook, an address outside the allow-list, a body over the hook's " +
      "cap or not whole within 15 s, too many uploads at once, an address with 20 bad " +
      "signatures in 10 minutes (refused for the window; the hook stays on and the managers are " +
      "told), and a bad or stale signature; the delivery log tells the managers which (within " +
      "its budget of 30 a minute per hook). Two answers come before the hook is looked at: a " +
      "declared length over 1 MiB is 413, and the API's per-address limit is 429 (not logged). Once verified: paused 503 with `Retry-After`; " +
      "disabled 410; not JSON 415; not an object or array 422; over the hook's or the company's " +
      "rate 429. The same delivery again (its delivery id, Stripe's event id, `Idempotency-Key`, or " +
      "the same signature) answers 202 `duplicate` with the first receipt and writes nothing. " +
      "Accepted: 202, the delivery logged with its body and `inbound_webhook.received` written " +
      "(never with the body); the delivery then becomes a task for the hook's DE.",
    access: "hook_signature",
    params: CompanyItemParams,
    body: z.unknown(),
    response: { status: 202, schema: InboundReceipt },
    errors: ["payload_too_large", "hook_paused", "hook_disabled", "unsupported_media_type"],
    // `inbound_webhook.signatures_failing` is written by a refused call (401):
    // `backgroundProducers` lists it.
    emits: ["inbound_webhook.received"],
  }),
} as const;
